import { AWS_ACCOUNT_PATTERN } from "@p0security/integrations-aws-shared/constants";
import { awsRoleName } from "@p0security/integrations-aws-shared/policy/shared";
import {
  INVALID_AWS_CONNECTOR_NAME,
  INVALID_GCP_CONNECTOR_NAME,
  isAwsLambdaEcrIamName,
  isGcpServiceAccountId,
} from "@p0security/integrations-harbor-shared/connector-hosting";
import { isGcpRegion } from "@p0security/integrations-harbor-shared/gcp-hosting";
import {
  INVALID_ITEM_ID,
  isItemId,
} from "@p0security/integrations-harbor-shared/item-id";
import { findSecretNameProblem } from "@p0security/integrations-harbor-shared/secret-name";
import type {
  HarborConnectorConfig,
  HarborInstanceConfig,
} from "@p0security/integrations-harbor-shared/types";
import { tfId } from "@p0security/integrations-vaulted-credential-shared/iac";
import type { DevEnv } from "@p0security/shared/types/environment";
import { ValidationError } from "@p0security/shared/types/error";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

// The Terraform each setup page shows, generated from the item it's for.
//
// Every value written into a snippet passes a rule first, so that none can
// end its string, start an interpolation or end the comment it's on: see
// each resolver (`toAwsConnector`, `toGcpConnector`, `toGcpReadGrant`,
// `toAwsReadGrant`).

/** One Terraform snippet on a setup page, and the paragraph above it. */
export type IacCommand = Readonly<{ command: string; header: ReactNode }>;

type AwsHosting = Extract<HarborConnectorConfig["hosting"], { type: "aws" }>;
type GcpHosting = Extract<HarborConnectorConfig["hosting"], { type: "gcp" }>;
type AwsVault = Extract<HarborInstanceConfig["vault"], { type: "aws-sm" }>;
type GcpVault = Extract<HarborInstanceConfig["vault"], { type: "gcp-sm" }>;

const { Paragraph, Text } = Typography;

/** What a connector's setup page says in place of a service account it can't
 * name: P0 hasn't stored one, and the connector has no project or name to
 * name one after. */
export const MISSING_SERVICE_ACCOUNT =
  "unknown, because the connector has no project or connector name";

/** The prefix of every Terraform name a connector item's Terraform declares.
 *
 * Distinct from an instance's, so a connector and an instance with the same
 * id don't collide. The id must already pass `isItemId`: it may start with a
 * digit, which the prefix makes a valid Terraform name. */
const connectorTfName = (id: string) => `p0_harbor_connector_${tfId(id)}`;

/** The prefix of every Terraform name an instance's Terraform for its
 * secret's account declares. A prefix of its own: `<instance prefix>_vault`
 * would be the instance prefix of another id, `<id>-vault`. The id must
 * already pass `isItemId`. */
const vaultTfName = (id: string) => `p0_harbor_vault_${tfId(id)}`;

/** The prefix of every Terraform name an instance item's Terraform declares.
 * The id must already pass `isItemId`. */
const instanceTfName = (id: string) => `p0_harbor_instance_${tfId(id)}`;

// Copied from github-repositories' connector Terraform, in
// packages/integrations/github-repositories/frontend/src/iac.ts, and kept
// close to it: `UNPUBLISHED_DIGEST`, `IMAGE_PIN_PATTERN` (its
// `IMAGE_DIGEST_PATTERN`), `matches`, `validateImagePin` (its
// `validateImageDigest`), `LAMBDA_SETTINGS`, `LOG_RETENTION_DAYS`,
// `POLICY_NAMES` and the Terraform template in `renderAwsConnectorTerraform`
// (its `renderAwsTerraform`).
// TODO(INT-432): share them, and drop these copies.

// The all-zero digest that stands in for an image before it's published.
const UNPUBLISHED_DIGEST = `sha256:${"0".repeat(64)}`;

// The connector build the AWS Terraform deploys, as `<tag>@sha256:<digest>`:
// a p0-security-perimeter main build's sha-<commit> tag on Docker Hub, and
// that tag's digest. To move to a newer build, change both, and find the
// digest with
// `docker buildx imagetools inspect p0security/p0-connector-harbor:<tag>`.
const AWS_IMAGE_PIN =
  "sha-7663c38@sha256:109e007f15e268af996ad00e055bac13d4499a14064cfb318b411d2b9655855b";

/** P0's Harbor connector image for Lambda, on Docker Hub. */
const LAMBDA_IMAGE = "p0security/p0-connector-harbor";

// Docker caps a tag at 128 characters, and the AWS Terraform tags each copy of
// the image with its tag, a hyphen and the copy's 36-character id.
const IMAGE_PIN_PATTERN = /^\w[\w.-]{0,90}@sha256:[a-f0-9]{64}$/;

// A copy of github-repositories' region rules, in
// packages/integrations/github-repositories/shared/src/hosting.ts
// (`AWS_REGION_PATTERN`, `AWS_OTHER_PARTITION_REGION_PATTERN`), kept close to
// them. databricks has a third copy, in
// packages/integrations/databricks/backend/src/install/index.ts.
// TODO(INT-432): share them, and drop these copies.
// The installer below refuses a bad account or region when the connector is
// created, with harbor/shared's `isAwsAccountId` and `findAwsRegionProblem`.
// This page checks them again before it renders the Terraform, with its own
// copies: those rules trim the value, and an instance's read grant checks its
// secret's account and region as stored, refusing a padded one.

/** An AWS region, such as `us-west-2`. */
const AWS_REGION_PATTERN = /^[a-z]+(?:-[a-z]+)+-\d+$/;

/** A region outside AWS's commercial partition: GovCloud, China, the ISO
 * regions or the European Sovereign Cloud. P0 invokes the connector by an
 * `arn:aws:` ARN, so it can't run in one. */
const AWS_OTHER_PARTITION_REGION_PATTERN =
  /^(?:us-gov-|cn-|[a-z]+-iso[a-z]*-|eusc-)/;

/** Whether `account` is an AWS account's ID: 12 digits. */
const isAwsAccountId = (account: string) => AWS_ACCOUNT_PATTERN.test(account);

/** Whether `region` has the form of an AWS region, in any partition. */
const isAwsRegion = (region: string) => AWS_REGION_PATTERN.test(region);

/** Whether `region`, which has the form of an AWS region, is in AWS's
 * commercial partition: one P0 can invoke a connector in, or point one at a
 * secret in. */
const isAwsCommercialPartition = (region: string) =>
  !AWS_OTHER_PARTITION_REGION_PATTERN.test(region);

// IAM's rule for a role name. P0's role is named from P0's environment, not
// from the item, but it's written into the Terraform all the same.
const IAM_ROLE_NAME_PATTERN = /^[\w+=,.@-]{1,64}$/;

/** The Lambda function's settings. */
const LAMBDA_SETTINGS = {
  architecture: "x86_64",
  memorySize: 512,
  timeout: 60,
} as const;

/** How many days CloudWatch keeps the connector's logs, unless the customer
 * changes it. */
const LOG_RETENTION_DAYS = 30;

/** The names of the inline policies the connector's Terraform puts on the
 * connector's role. An instance's read grant is another inline policy on the
 * same role, so it needs a name that isn't one of these. */
const POLICY_NAMES = { logs: "WriteConnectorLogs" } as const;

/** What a refusal tells the customer to do. A connector's fields are fixed
 * when it's created, so there's nothing to correct in place. */
const REINSTALL = "Delete this connector and add it again";

/** Whether `value` is a string that `pattern` matches. `pattern.test` alone
 * would test a missing value as the string "undefined". */
const matches = (pattern: RegExp, value: unknown): value is string =>
  typeof value === "string" && pattern.test(value);

/** The name of the Terraform variable that holds the email domain pattern of
 * the connector item `id`. Each connector has its own, because several can
 * share a configuration. */
const toPatternVariableName = (id: string) =>
  `${connectorTfName(id)}_allowed_email_domain_pattern`;

/** The Terraform variable `patternVariable`, which holds a connector's email
 * domain pattern: required, and never blank. */
const renderPatternVariable = (
  patternVariable: string
) => `# The connector gives Harbor access only to users whose whole email domain
# matches this. P0 never stores or sends it.
variable "${patternVariable}" {
  description = "A regular expression that an email's whole domain must match for its user to receive Harbor access through this connector, such as example[.]com."
  type        = string
  nullable    = false

  validation {
    condition     = trimspace(var.${patternVariable}) != ""
    error_message = "Set the email domain pattern. The connector refuses every request without one."
  }
}`;

/** The `required_providers` block of a connector's Terraform. Both clouds'
 * are the same, so that one block serves a configuration that holds both.
 *
 * 3.3.0 is the first release of the docker provider with
 * `disable_docker_daemon_check`, which the Google Cloud Terraform sets. Both
 * clouds' Terraform validates on 3.3.0, 3.9.0 and 4.6.0. */
const REQUIRED_PROVIDERS = `terraform {
  # If your configuration already has a required_providers block, add docker to
  # it, at 3.3 or later, and delete this block.
  required_providers {
    docker = {
      source  = "kreuzwerker/docker"
      version = ">= 3.3, < 5.0"
    }
  }
}`;

/** The data source that reads a connector image's digest from Docker Hub, and
 * fails the plan unless it's the pinned one.
 *
 * @param args.image The image's repository on Docker Hub
 * @param args.providerAlias The alias of the docker provider to read it with,
 *   or nothing for the configuration's default one
 * @param args.tfName The connector's Terraform name
 */
const renderImageDigestCheck = (args: {
  image: string;
  providerAlias?: string;
  tfName: string;
}) => {
  const { image, providerAlias, tfName } = args;
  const provider = providerAlias
    ? `  provider = docker.${providerAlias}\n\n`
    : "";
  return `# Stops the plan if the tag has been moved to another image since P0 pinned
# it, so that only the image P0 pinned is deployed.
data "docker_registry_image" "${tfName}" {
${provider}  name = "${image}:\${local.${tfName}.image_tag}"

  lifecycle {
    postcondition {
      condition     = self.sha256_digest == local.${tfName}.image_digest
      error_message = "The connector image's tag doesn't resolve to the digest pinned here. Copy the current Terraform from P0's Harbor installer, or contact support@p0.dev."
    }
  }
}`;
};

/** The arguments to {@link renderAwsConnector}. */
export type AwsConnectorArgs = Readonly<{
  /** The environment, which names P0's AWS role. An install context is one. */
  context: DevEnv;
  hosting: AwsHosting;
  /** The connector item's id */
  id: string;
  /** The connector image to deploy, as `<tag>@sha256:<digest>`. Defaults to
   * the build P0 pins. */
  imagePin?: string;
}>;

/** A connector on AWS, as its Terraform deploys it. Each value is trimmed, as
 * P0 stores it, and has passed the rule that makes it safe in the
 * Terraform. */
type AwsConnector = Readonly<{
  /** The account the connector runs in */
  accountId: string;
  connectorName: string;
  connectorRegion: string;
  /** The connector item's id */
  id: string;
  /** The digest that `imageTag` resolves to on Docker Hub */
  imageDigest: string;
  imageTag: string;
  /** The role P0 assumes in the connector's account, which invokes it */
  p0RoleName: string;
}>;

/** `value`, if it's a string that passes `test`; otherwise throws a
 * {@link ValidationError} for `field` with `message`. Neither the error's
 * state nor its message holds the value.
 *
 * A connector's field is checked and returned trimmed, as P0 stores it.
 *
 * @param args.exact Checks the value as it stands, so that padding is
 *   refused. For a value P0 also sends the connector as it stands: trimming
 *   it here would grant on another name than the connector is sent.
 */
const validateField = (args: {
  exact?: boolean;
  field: string;
  message: string;
  test: (value: string) => boolean;
  value: unknown;
}) => {
  const { exact = false, field, message, test, value } = args;
  const checked =
    typeof value !== "string" ? undefined : exact ? value : value.trim();
  if (checked === undefined || !test(checked)) {
    throw new ValidationError(field, {}, message);
  }
  return checked;
};

/** Throws a {@link ValidationError} unless `id` can be part of a Terraform
 * name. The setup page refuses a bad id before it renders anything; this
 * guards the Terraform names on their own.
 *
 * @param args.reinstall What the refusal tells the customer to do
 */
const validateItemId = (args: { id: string; reinstall: string }) => {
  const { id, reinstall } = args;
  if (!isItemId(id)) {
    throw new ValidationError(
      "id",
      {},
      `This ID can't name Terraform resources. ${reinstall} with another ID. ${INVALID_ITEM_ID}`
    );
  }
};

/** What each refusal about an AWS connector's hosting says, by the page it's
 * shown on: the connector's own, or that of an instance that uses it. Each
 * ends with what to delete and add again, since a connector's fields can't
 * be corrected in place. */
type AwsConnectorRoleCopy = Readonly<{
  account: string;
  /** The form id of the field `name` of the connector's hosting */
  field: (name: "accountId" | "connectorName" | "connectorRegion") => string;
  name: string;
  region: string;
  unsupportedRegion: string;
}>;

/**
 * Where an AWS connector runs and the role it runs as, which is named after
 * it: what the connector's own Terraform creates, and what an instance's
 * read grant names. Both resolve it here, so they can't differ. Each value
 * is trimmed, as P0 stores it.
 *
 * @throws ValidationError, worded by `copy`, for an account that isn't 12
 * digits, a name that can't name the role, or a region that isn't one or is
 * outside AWS's commercial partition
 */
const toAwsConnectorRole = (args: {
  copy: AwsConnectorRoleCopy;
  hosting: AwsHosting;
}) => {
  const { copy, hosting } = args;
  const accountId = validateField({
    field: copy.field("accountId"),
    message: copy.account,
    test: isAwsAccountId,
    value: hosting.accountId,
  });
  const connectorName = validateField({
    field: copy.field("connectorName"),
    message: copy.name,
    test: isAwsLambdaEcrIamName,
    value: hosting.connectorName,
  });
  const connectorRegion = validateField({
    field: copy.field("connectorRegion"),
    message: copy.region,
    test: isAwsRegion,
    value: hosting.connectorRegion,
  });
  if (!isAwsCommercialPartition(connectorRegion)) {
    throw new ValidationError(
      copy.field("connectorRegion"),
      {},
      copy.unsupportedRegion
    );
  }
  return { accountId, connectorName, connectorRegion };
};

/** The tag and the digest of `imagePin`, a `<tag>@sha256:<digest>`.
 *
 * @throws ValidationError if it's the placeholder of an image that isn't
 * published yet, which the customer has to wait out
 * @throws Error if it isn't a pin at all, which is P0's mistake
 */
const validateImagePin = (imagePin: unknown) => {
  if (!matches(IMAGE_PIN_PATTERN, imagePin)) {
    throw new Error("The connector image must be <tag>@sha256:<digest>.");
  }
  const [imageTag, imageDigest] = imagePin.split("@");
  if (imageDigest === UNPUBLISHED_DIGEST) {
    throw new ValidationError(
      "imagePin",
      {},
      "P0 hasn't published the Harbor connector image yet, so it can't give you the connector's Terraform."
    );
  }
  return { imageDigest, imageTag };
};

/**
 * Resolves the connector that one connector item's AWS Terraform deploys.
 *
 * Every value it returns is written into the Terraform, so each must pass a
 * rule that keeps it inside its string: none of them can hold a quote, a
 * backslash, a line break or `${`.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input the connector can't be deployed with: an id or a hosting field P0
 * would refuse, a region outside AWS's commercial partition, or an image that
 * isn't published yet. Its message is for the customer.
 * @throws Error for P0's own mistakes: an image pin that isn't one, or an
 * environment that gives P0's role a name IAM wouldn't take.
 */
const toAwsConnector = (args: AwsConnectorArgs): AwsConnector => {
  const { context, hosting, id, imagePin = AWS_IMAGE_PIN } = args;
  validateItemId({ id, reinstall: REINSTALL });
  const { accountId, connectorName, connectorRegion } = toAwsConnectorRole({
    copy: {
      account: `This connector's AWS account ID isn't 12 digits. ${REINSTALL}.`,
      field: (name) => `hosting.${name}`,
      name: `This connector's name can't name a Lambda function and its role. ${REINSTALL} with another name. ${INVALID_AWS_CONNECTOR_NAME}`,
      region: `This connector's region isn't an AWS region, such as us-west-2. ${REINSTALL}.`,
      unsupportedRegion: `Harbor supports only AWS commercial regions for now. ${REINSTALL} in one.`,
    },
    hosting,
  });
  const { imageDigest, imageTag } = validateImagePin(imagePin);
  const p0RoleName = awsRoleName(context, "iam-write");
  if (!IAM_ROLE_NAME_PATTERN.test(p0RoleName)) {
    // P0's environment names the role, so the customer can do nothing about
    // it: not a refusal the setup page shows them.
    throw new Error("P0's environment gives its AWS role an invalid name.");
  }
  return {
    accountId,
    connectorName,
    connectorRegion,
    id,
    imageDigest,
    imageTag,
    p0RoleName,
  };
};

/**
 * Renders the Terraform that deploys one connector item's connector on AWS.
 *
 * It copies the image into ECR and deploys a Lambda function, outside any VPC,
 * as a role named after the connector. The role can write the connector's own
 * logs and nothing else: each Harbor instance that uses the connector attaches
 * its own read grant to it, from its own Terraform. P0's role can invoke the
 * function. An output gives where the connector runs, for anything that has
 * to wait until P0 can invoke it.
 *
 * Every Terraform name carries the item's id, so several connectors, and the
 * instances' grants, can share a configuration once their `required_providers`
 * blocks are merged.
 */
const renderAwsConnectorTerraform = (connector: AwsConnector): string => {
  const {
    accountId,
    connectorName,
    connectorRegion,
    id: itemId,
    imageDigest,
    imageTag,
    p0RoleName,
  } = connector;
  const tfName = connectorTfName(itemId);
  const patternVariable = toPatternVariableName(itemId);

  return `# P0's Harbor connector ${itemId}, on AWS Lambda.
#
# Applying this copies P0's connector image from Docker Hub into an ECR
# repository, which Lambda requires. Run terraform where Docker and the AWS CLI
# are installed and the AWS CLI is signed in to account ${accountId}.
#
# Other connectors can share this configuration, but each needs its own
# connector name: the connector's role, repository and log group are named
# after it, and these names are unique in an AWS account.
#
# Set ${patternVariable}
# when you apply, with -var or a .tfvars file.
#
# Before you destroy this, destroy the read grant of each Harbor instance that
# uses this connector. Each grant is a policy on the connector's role, and
# Terraform doesn't remove a role's other policies when it destroys the role,
# so the destroy fails while one is left.

terraform {
  # terraform_data needs Terraform 1.4.
  required_version = ">= 1.4"
}

${REQUIRED_PROVIDERS}

${renderPatternVariable(patternVariable)}

locals {
  ${tfName} = {
    # Where P0 invokes the connector. P0's installer has the same values.
    account_id       = "${accountId}"
    connector_name   = "${connectorName}"
    connector_region = "${connectorRegion}"
    # How many days CloudWatch keeps the connector's logs. Change it if you
    # need to.
    log_retention_days = ${LOG_RETENTION_DAYS}
    # The role P0 assumes in this account, which invokes the connector.
    p0_role_name = "${p0RoleName}"
    # P0's connector image: its tag on Docker Hub, and the digest that tag must
    # resolve to.
    image_tag    = "${imageTag}"
    image_digest = "${imageDigest}"
  }
}

# P0 derives the connector's ARN from the account, region and name above, so the
# connector must be deployed exactly there. Add the credential settings your
# other AWS providers use, such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${tfName}"
  region              = local.${tfName}.connector_region
  allowed_account_ids = [local.${tfName}.account_id]
}

${renderImageDigestCheck({ image: LAMBDA_IMAGE, tfName })}

resource "aws_ecr_repository" "${tfName}" {
  provider = aws.${tfName}

  name = local.${tfName}.connector_name
  # Tags can't be overwritten, so no one who can push to this repository can
  # replace the image that runs as the connector.
  image_tag_mutability = "IMMUTABLE"
  force_delete         = true

  image_scanning_configuration {
    scan_on_push = true
  }
}

# Copies the pinned image from Docker Hub into the ECR repository, tagged with
# the image's tag and this copy's id. ECR's tags can't be overwritten, so each
# copy needs a tag of its own: copying the image again, to roll back or after
# an interrupted apply, pushes a new one.
resource "terraform_data" "${tfName}_image" {
  triggers_replace = {
    digest         = data.docker_registry_image.${tfName}.sha256_digest
    repository_url = aws_ecr_repository.${tfName}.repository_url
    tag            = local.${tfName}.image_tag
  }

  provisioner "local-exec" {
    # Signs Docker in to ECR in a configuration directory of its own, which it
    # deletes at the end. So the login doesn't outlast the copy, connectors
    # copied at the same time don't share one, and your own Docker logins are
    # left alone. The Docker daemon is still the one your Docker context
    # points at.
    command = <<-EOT
      set -e
      if [ -z "$DOCKER_HOST" ]; then
        DOCKER_HOST="$(docker context inspect --format '{{ .Endpoints.docker.Host }}')"
        export DOCKER_HOST
      fi
      DOCKER_CONFIG="$(mktemp -d)"
      export DOCKER_CONFIG
      trap 'rm -rf "$DOCKER_CONFIG"' EXIT
      aws ecr get-login-password --region "$REGION" |
        docker login --username AWS --password-stdin "$REGISTRY"
      docker pull --platform linux/amd64 "$SOURCE"
      docker tag "$SOURCE" "$TARGET"
      docker push "$TARGET"
    EOT

    environment = {
      REGION   = local.${tfName}.connector_region
      REGISTRY = split("/", aws_ecr_repository.${tfName}.repository_url)[0]
      SOURCE   = "${LAMBDA_IMAGE}@\${local.${tfName}.image_digest}"
      TARGET   = "\${aws_ecr_repository.${tfName}.repository_url}:\${local.${tfName}.image_tag}-\${self.id}"
    }
  }
}

# Pulling one platform and pushing it gives ECR that image's own manifest, not
# the tag's multi-platform one, so the digest there can differ from the one
# pinned above. Lambda needs ECR's.
data "aws_ecr_image" "${tfName}" {
  provider = aws.${tfName}

  repository_name = aws_ecr_repository.${tfName}.name
  image_tag       = "\${local.${tfName}.image_tag}-\${terraform_data.${tfName}_image.id}"
}

# The identity the connector runs as, named after the connector. Each Harbor
# instance that uses the connector attaches its own policy to it, from its own
# Terraform, so this role has no inline_policy block and nothing here claims
# its policies exclusively: either would delete those grants on the next
# apply.
resource "aws_iam_role" "${tfName}" {
  provider = aws.${tfName}

  name = local.${tfName}.connector_name
  assume_role_policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect    = "Allow"
      Principal = { Service = "lambda.amazonaws.com" }
      Action    = "sts:AssumeRole"
    }]
  })
}

# The log group Lambda writes the connector's logs to.
resource "aws_cloudwatch_log_group" "${tfName}" {
  provider = aws.${tfName}

  name              = "/aws/lambda/\${local.${tfName}.connector_name}"
  retention_in_days = local.${tfName}.log_retention_days
}

# The connector writes its own logs, and no others.
resource "aws_iam_role_policy" "${tfName}_logs" {
  provider = aws.${tfName}

  name = "${POLICY_NAMES.logs}"
  role = aws_iam_role.${tfName}.id
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = ["logs:CreateLogStream", "logs:PutLogEvents"]
      Resource = "\${aws_cloudwatch_log_group.${tfName}.arn}:*"
    }]
  })
}

# No vpc_config: the connector calls your Harbor instances over the internet.
resource "aws_lambda_function" "${tfName}" {
  provider = aws.${tfName}

  function_name = local.${tfName}.connector_name
  description   = "P0 Harbor connector ${itemId}"
  role          = aws_iam_role.${tfName}.arn
  package_type  = "Image"
  image_uri     = "\${aws_ecr_repository.${tfName}.repository_url}@\${data.aws_ecr_image.${tfName}.image_digest}"
  architectures = ["${LAMBDA_SETTINGS.architecture}"]
  memory_size   = ${LAMBDA_SETTINGS.memorySize}
  timeout       = ${LAMBDA_SETTINGS.timeout}

  environment {
    variables = {
      ALLOWED_EMAIL_DOMAIN_PATTERN = var.${patternVariable}
    }
  }

  # The connector's role can't create a log group, so its own must exist before
  # it runs.
  depends_on = [aws_cloudwatch_log_group.${tfName}]
}

# P0 invokes the connector as its role in this account, and needs nothing else
# on it.
resource "aws_iam_role_policy" "${tfName}_invoke" {
  provider = aws.${tfName}

  name = "\${local.${tfName}.connector_name}-invoke"
  role = local.${tfName}.p0_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "lambda:InvokeFunction"
      Resource = aws_lambda_function.${tfName}.arn
    }]
  })
}

# Where the connector runs, as P0's installer has it. It's known only once P0
# can invoke the connector, so anything that waits for the connector can
# depend on it.
output "${tfName}" {
  value = {
    account_id       = local.${tfName}.account_id
    connector_name   = aws_lambda_function.${tfName}.function_name
    connector_region = local.${tfName}.connector_region
  }

  depends_on = [aws_iam_role_policy.${tfName}_invoke]
}
`;
};

/** The connector on AWS: its image, the role it runs as, the Lambda function,
 * and P0's permission to invoke it.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the connector can't be deployed with: an id or a hosting field P0
 * would refuse, a region outside AWS's commercial partition, or an image that
 * isn't published yet
 * @throws Error for P0's own mistakes: an image pin that isn't one, or an
 * environment that gives P0's role a name IAM wouldn't take
 */
export const renderAwsConnector = (args: AwsConnectorArgs): IacCommand => {
  const connector = toAwsConnector(args);
  const patternVariable = toPatternVariableName(connector.id);
  return {
    command: renderAwsConnectorTerraform(connector),
    header: (
      <Paragraph>
        Deploys the connector as the Lambda function{" "}
        <Text code>{connector.connectorName}</Text> in AWS account{" "}
        {connector.accountId}, in {connector.connectorRegion}, and lets P0
        invoke it. Apply it where Docker and the AWS CLI are installed. Set{" "}
        <Text code>{patternVariable}</Text> to a regular expression that each
        user&apos;s whole email domain must match, such as{" "}
        <Text code>example[.]com</Text>. P0 never stores or sends it.
      </Paragraph>
    ),
  };
};

// The connector build the Google Cloud Terraform deploys, pinned as the AWS
// one is: find the digest with
// `docker buildx imagetools inspect p0security/p0-connector-harbor-gcloud:<tag>`.
const GCP_IMAGE_PIN =
  "sha-7663c38@sha256:93328ee49abd6ae9ae76e76d53075456f1be3e6eaf494aeba358a7b2eeab7fd2";

/** P0's Harbor connector image for Cloud Run, on Docker Hub. */
const CLOUD_RUN_IMAGE = "p0security/p0-connector-harbor-gcloud";

/** A Google Cloud project's ID: 6 to 30 lowercase letters, digits and
 * hyphens, starting with a letter and not ending with a hyphen. Not a project
 * number, and not a project's name. */
// The installer below checks the project when the connector is created, with
// gcloud's looser rule for a project, which this one is stricter than.
// harbor/shared has no rule for a project ID to use here instead.
const GCP_PROJECT_ID_PATTERN = /^[a-z][a-z0-9-]{4,28}[a-z0-9]$/;

// P0's own service account comes from P0's pool of them, by way of the Google
// Cloud integration's install, not from the item. Nothing here knows which
// forms the pool holds, so this checks only that it's an email whose
// characters are safe in the Terraform.
const INVOKER_EMAIL_PATTERN = /^[\w.+-]+@[\w.-]+$/;

/** The arguments to {@link renderGcpConnector}. */
export type GcpConnectorArgs = Readonly<{
  hosting: GcpHosting;
  /** The connector item's id */
  id: string;
  /** The connector image to deploy, as `<tag>@sha256:<digest>`. Defaults to
   * the build P0 pins. */
  imagePin?: string;
  /** P0's own service account, or "" before the Google Cloud integration is
   * installed */
  invokerEmail: string;
}>;

/** A connector on Google Cloud, as its Terraform deploys it. Each value is
 * trimmed, as P0 stores it, and has passed the rule that makes it safe in
 * the Terraform. */
type GcpConnector = Readonly<{
  /** The Cloud Run service's name */
  connectorName: string;
  connectorRegion: string;
  /** The connector item's id */
  id: string;
  /** The digest that `imageTag` resolves to on Docker Hub */
  imageDigest: string;
  imageTag: string;
  /** P0's own service account, which invokes the connector */
  invokerEmail: string;
  projectId: string;
  /** The ID of the service account the service runs as, in `projectId` */
  serviceAccountId: string;
}>;

/**
 * The ID of the service account a connector runs as.
 *
 * It's the account P0 stored for the connector, which each instance's read
 * grant names too. P0 stores it when it saves the connector, named after the
 * connector, so before then it's the connector's name.
 *
 * @param args.unusable What to tell the customer when the stored account
 *   can't be used
 * @throws ValidationError if the stored account isn't a service account, with
 * an ID a connector's can have, in the connector's project
 */
const toServiceAccountId = (args: {
  connectorName: string;
  projectId: string;
  storedServiceAccount: unknown;
  unusable: Readonly<{ field: string; message: string }>;
}) => {
  const { connectorName, projectId, storedServiceAccount, unusable } = args;
  if (storedServiceAccount === undefined || storedServiceAccount === "") {
    return connectorName;
  }
  const domain = `@${projectId}.iam.gserviceaccount.com`;
  const accountId =
    typeof storedServiceAccount === "string" &&
    storedServiceAccount.endsWith(domain)
      ? storedServiceAccount.slice(0, -domain.length)
      : undefined;
  if (
    accountId === undefined ||
    // The ID is checked as it stands: an email isn't trimmed.
    accountId !== accountId.trim() ||
    !isGcpServiceAccountId(accountId)
  ) {
    throw new ValidationError(unusable.field, {}, unusable.message);
  }
  return accountId;
};

/**
 * Resolves the connector that one connector item's Google Cloud Terraform
 * deploys.
 *
 * Every value it returns is written into the Terraform, so each must pass a
 * rule that keeps it inside its string: none of them can hold a quote, a
 * backslash, a line break or `${`.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input the connector can't be deployed with: an id or a hosting field P0
 * would refuse, a stored service account that isn't one in the connector's
 * project, no invoker because P0's Google Cloud integration isn't installed,
 * or an image that isn't published yet. Its message is for the customer.
 * @throws Error for P0's own mistakes: an image pin that isn't one, or an
 * invoker that isn't an email.
 */
const toGcpConnector = (args: GcpConnectorArgs): GcpConnector => {
  const { hosting, id, imagePin = GCP_IMAGE_PIN, invokerEmail } = args;
  validateItemId({ id, reinstall: REINSTALL });
  const projectId = validateField({
    field: "hosting.projectId",
    message: `This connector's project isn't a Google Cloud project ID: 6 to 30 lowercase letters, digits and hyphens, not a project's number or name. ${REINSTALL}.`,
    test: (project) => GCP_PROJECT_ID_PATTERN.test(project),
    value: hosting.projectId,
  });
  const connectorName = validateField({
    field: "hosting.connectorName",
    message: `This connector's name can't name a Cloud Run service and its service account. ${REINSTALL} with another name. ${INVALID_GCP_CONNECTOR_NAME}`,
    test: isGcpServiceAccountId,
    value: hosting.connectorName,
  });
  const connectorRegion = validateField({
    field: "hosting.connectorRegion",
    message: `This connector's region isn't a Google Cloud region, such as us-west1. ${REINSTALL}.`,
    test: isGcpRegion,
    value: hosting.connectorRegion,
  });
  const serviceAccountId = toServiceAccountId({
    connectorName,
    projectId,
    storedServiceAccount: hosting.connectorServiceAccount,
    unusable: {
      field: "hosting.connectorServiceAccount",
      message: `The service account P0 holds for this connector isn't one in the connector's project. ${REINSTALL}.`,
    },
  });
  if (!invokerEmail) {
    throw new ValidationError(
      "invokerEmail",
      {},
      "Install P0's Google Cloud IAM management first, then return here. The connector accepts calls only from the service account it gives P0."
    );
  }
  if (!matches(INVOKER_EMAIL_PATTERN, invokerEmail)) {
    // P0's Google Cloud install names it, so the customer can do nothing
    // about it: not a refusal whose message is for them.
    throw new Error(
      "P0's Google Cloud integration holds an invoker that isn't an email."
    );
  }
  const { imageDigest, imageTag } = validateImagePin(imagePin);
  return {
    connectorName,
    connectorRegion,
    id,
    imageDigest,
    imageTag,
    invokerEmail,
    projectId,
    serviceAccountId,
  };
};

// The Cloud Run blocks below are a copy of `gcpConnectorTf`'s, in
// packages/integrations/vaulted-credential/shared/src/iac.ts:
// `google_project_service`, `google_service_account`,
// `google_cloud_run_v2_service` with `INVOKER_SA_EMAIL`, and the invoker and
// viewer `google_cloud_run_v2_service_iam_member`s. Changed on purpose: two
// APIs, not four; no secret, custom role or project binding, which the
// original always creates; each grant's `location` and `name` read off the
// service; and names that carry the item's id. The image's digest check and
// the email domain pattern are Harbor's additions.
// TODO(INT-432): share them, and drop this copy.

/**
 * Renders the Terraform that deploys one connector item's connector on Google
 * Cloud.
 *
 * It enables the two APIs the connector needs and deploys P0's image from
 * Docker Hub as a Cloud Run service, running as the service account P0 holds
 * for the connector. The account has no role: each Harbor instance that uses
 * the connector grants it read on that instance's secret, from its own
 * Terraform. P0's service account can invoke the service and read it, and
 * nothing here lets anyone else. An output gives where the connector runs,
 * for anything that has to wait until P0 can invoke it.
 *
 * Every Terraform name carries the item's id, so several connectors, and the
 * instances' grants, can share a configuration once their `required_providers`
 * blocks are merged.
 */
const renderGcpConnectorTerraform = (connector: GcpConnector): string => {
  const {
    connectorName,
    connectorRegion,
    id: itemId,
    imageDigest,
    imageTag,
    invokerEmail,
    projectId,
    serviceAccountId,
  } = connector;
  const tfName = connectorTfName(itemId);
  const patternVariable = toPatternVariableName(itemId);

  return `# P0's Harbor connector ${itemId}, on Google Cloud Run.
#
# It needs Terraform's google provider at 6.0 or later, and the Service Usage
# API enabled in project ${projectId},
# which lets Terraform enable the IAM and Cloud Run APIs. If the first apply
# fails right after it enables them, apply again: a newly enabled API can take
# a few minutes to answer.
#
# Cloud Run pulls P0's connector image from Docker Hub. Terraform reads the
# image's digest from Docker Hub on every plan, so the machine that runs
# Terraform has to reach it. It needs no Docker. If the image is missing, or
# Docker Hub can't be reached or limits your requests, the plan fails with the
# docker provider's own error.
#
# Other connectors can share this configuration, but each needs its own
# connector name: the connector's service is named after it.
#
# Set ${patternVariable}
# when you apply, with -var or a .tfvars file.
#
# Destroying this deletes the connector's service account. Destroy the read
# grant of each Harbor instance that uses this connector too. A grant left
# behind names an account that no longer exists: remove it with that
# instance's Terraform, or from the secret's permissions.

terraform {
  # The image's digest check needs Terraform 1.2.
  required_version = ">= 1.2"
}

${REQUIRED_PROVIDERS}

${renderPatternVariable(patternVariable)}

locals {
  ${tfName} = {
    # Where P0 invokes the connector. P0's installer has the same values.
    project_id       = "${projectId}"
    connector_name   = "${connectorName}"
    connector_region = "${connectorRegion}"
    # The ID of the service account the connector runs as, which P0's
    # installer shows.
    service_account_id = "${serviceAccountId}"
    # P0's own service account. The connector accepts calls from it alone.
    invoker_email = "${invokerEmail}"
    # P0's connector image: its tag on Docker Hub, and the digest that tag must
    # resolve to.
    image_tag    = "${imageTag}"
    image_digest = "${imageDigest}"
  }
}

# Reads the image's digest from Docker Hub. It never talks to a Docker daemon,
# so none has to be running.
provider "docker" {
  alias                       = "${tfName}"
  disable_docker_daemon_check = true
}

${renderImageDigestCheck({ image: CLOUD_RUN_IMAGE, providerAlias: tfName, tfName })}

# The IAM and Cloud Run APIs. Never disabled on destroy: other services in the
# project may use them.
resource "google_project_service" "${tfName}" {
  for_each = toset(["iam.googleapis.com", "run.googleapis.com"])

  project            = local.${tfName}.project_id
  service            = each.key
  disable_on_destroy = false
}

# The identity the connector runs as: the service account P0 holds for this
# connector. It has no role here: each Harbor instance that uses the connector
# grants it read on that instance's secret, from its own Terraform.
#
# Don't destroy and recreate it. Google treats a recreated account as a new
# one, even under the same name: it doesn't get the old account's roles, and
# each instance's grant keeps naming the deleted account until that instance's
# Terraform is applied again. The connector may need redeploying too.
# https://cloud.google.com/iam/docs/service-accounts-delete-undelete
resource "google_service_account" "${tfName}" {
  project      = local.${tfName}.project_id
  account_id   = local.${tfName}.service_account_id
  display_name = "P0 Harbor connector ${itemId}"

  depends_on = [google_project_service.${tfName}]
}

resource "google_cloud_run_v2_service" "${tfName}" {
  project             = local.${tfName}.project_id
  name                = local.${tfName}.connector_name
  location            = local.${tfName}.connector_region
  deletion_protection = false
  # P0 calls the connector from outside Google Cloud; IAM, not origin, gates
  # access.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.${tfName}.email

    containers {
      image = "docker.io/${CLOUD_RUN_IMAGE}:\${local.${tfName}.image_tag}@\${data.docker_registry_image.${tfName}.sha256_digest}"

      # The connector checks every caller's token against this, and refuses
      # every call while it's unset, so it is not redundant with the
      # roles/run.invoker grant.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = local.${tfName}.invoker_email
      }

      env {
        name  = "ALLOWED_EMAIL_DOMAIN_PATTERN"
        value = var.${patternVariable}
      }
    }
  }

  depends_on = [google_project_service.${tfName}]
}

# P0 invokes the connector as its own service account. Nothing here lets
# anyone else invoke it.
resource "google_cloud_run_v2_service_iam_member" "${tfName}_invoker" {
  project  = local.${tfName}.project_id
  location = google_cloud_run_v2_service.${tfName}.location
  name     = google_cloud_run_v2_service.${tfName}.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:\${local.${tfName}.invoker_email}"
}

# P0 reads the service to get its invocation URL when it installs. The invoker
# role gives run.routes.invoke only, so it cannot do this read.
resource "google_cloud_run_v2_service_iam_member" "${tfName}_viewer" {
  project  = local.${tfName}.project_id
  location = google_cloud_run_v2_service.${tfName}.location
  name     = google_cloud_run_v2_service.${tfName}.name
  role     = "roles/run.viewer"
  member   = "serviceAccount:\${local.${tfName}.invoker_email}"
}

# Where the connector runs, as P0's installer has it. Anything that has to
# wait until P0 has been granted the connector can depend on this. A new
# grant can still take a minute or two to take effect.
output "${tfName}" {
  value = {
    project_id       = local.${tfName}.project_id
    connector_name   = google_cloud_run_v2_service.${tfName}.name
    connector_region = google_cloud_run_v2_service.${tfName}.location
  }

  depends_on = [
    google_cloud_run_v2_service_iam_member.${tfName}_invoker,
    google_cloud_run_v2_service_iam_member.${tfName}_viewer,
  ]
}
`;
};

/** The connector on Google Cloud: the APIs it needs, the service account it
 * runs as, the Cloud Run service, and P0's permission to invoke and read it.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the connector can't be deployed with: an id or a hosting field P0
 * would refuse, a stored service account that isn't one in the connector's
 * project, no invoker because P0's Google Cloud integration isn't installed,
 * or an image that isn't published yet
 * @throws Error for P0's own mistakes: an image pin that isn't one, or an
 * invoker that isn't an email
 */
export const renderGcpConnector = (args: GcpConnectorArgs): IacCommand => {
  const connector = toGcpConnector(args);
  const patternVariable = toPatternVariableName(connector.id);
  return {
    command: renderGcpConnectorTerraform(connector),
    header: (
      <Paragraph>
        Deploys the connector as the Cloud Run service{" "}
        <Text code>{connector.connectorName}</Text> in Google Cloud project{" "}
        {connector.projectId}, in {connector.connectorRegion}, running as the
        service account <Text code>{connector.serviceAccountId}</Text>, and lets
        P0 invoke it. The project needs the Service Usage API enabled, and the
        machine that runs Terraform has to reach Docker Hub. Set{" "}
        <Text code>{patternVariable}</Text> to a regular expression that each
        user&apos;s whole email domain must match, such as{" "}
        <Text code>example[.]com</Text>. P0 never stores or sends it. Don&apos;t
        destroy and recreate the service account: a recreated account
        doesn&apos;t get the old one&apos;s roles, so every Harbor
        instance&apos;s secret grant would have to be applied again, and the
        connector may need redeploying.
      </Paragraph>
    ),
  };
};

/** What an instance's refusal tells the customer to do. An instance's fields
 * are fixed when it's created, so there's nothing to correct in place. */
const REINSTALL_INSTANCE = "Delete this instance and add it again";

/** What a refusal tells the customer to do when an instance's connector is
 * what's wrong. A connector can't be corrected in place either, and its
 * instances name it, so they go first. */
const REINSTALL_CONNECTOR =
  "Delete this instance, and every other instance that uses the connector, then delete the connector. Add the connector again, then each instance.";

/** The arguments to {@link renderGcpReadGrant}. */
export type GcpReadGrantArgs = Readonly<{
  /** The id of the connector item the instance names */
  connectorId: string;
  /** Where that connector runs */
  hosting: GcpHosting;
  /** The instance item's id */
  id: string;
  secretName: string;
  vault: GcpVault;
}>;

/** An instance's read grant on Google Cloud, as its Terraform declares it.
 * Each value has passed the rule that makes it safe in the Terraform. */
type GcpReadGrant = Readonly<{
  /** The id of the instance's connector item */
  connectorId: string;
  /** The service account the connector runs as */
  connectorServiceAccount: string;
  /** The instance item's id */
  id: string;
  /** The secret's ID in `secretsProjectId` */
  secretName: string;
  secretsProjectId: string;
}>;

/**
 * Resolves the read grant that one instance item's Google Cloud Terraform
 * declares.
 *
 * Every value it returns is written into the Terraform, so each must pass a
 * rule that keeps it inside its string: none of them can hold a quote, a
 * backslash, a line break or `${`.
 *
 * The connector's service account is resolved as the connector's own
 * Terraform resolves it, so the two name one account: the one P0 stored, in
 * the connector's project, or the one named after the connector.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input the grant can't be declared with: an id, a project or a secret name
 * that isn't one, or a connector whose service account P0 can't tell or
 * can't use. Its message is for the customer.
 */
const toGcpReadGrant = (args: GcpReadGrantArgs): GcpReadGrant => {
  const { connectorId, hosting, id, secretName: name, vault } = args;
  validateItemId({ id, reinstall: REINSTALL_INSTANCE });
  const secretsProjectId = validateField({
    exact: true,
    field: "vault.install",
    message: `This instance's secret isn't in a project with a Google Cloud project ID: 6 to 30 lowercase letters, digits and hyphens, not a project's number or name. ${REINSTALL_INSTANCE}.`,
    test: (project) => GCP_PROJECT_ID_PATTERN.test(project),
    value: vault.install,
  });
  const secretName = validateField({
    exact: true,
    field: "secretName",
    message: `This instance's secret name can't name a secret in Google Secret Manager, which takes letters, digits, underscores and hyphens, up to 255 characters. ${REINSTALL_INSTANCE}.`,
    test: (secret) =>
      findSecretNameProblem(secret, { vault: "gcp-sm" }) === undefined,
    value: name,
  });
  // Before P0 stores an account for the connector, it's the one named after
  // the connector, in the connector's project.
  const unknown = `P0 can't tell which service account this instance's connector runs as, because the connector's ID, project or name isn't usable. ${REINSTALL_CONNECTOR}`;
  if (!isItemId(connectorId)) {
    throw new ValidationError("connector", {}, unknown);
  }
  const projectId = validateField({
    field: "connector",
    message: unknown,
    test: (project) => GCP_PROJECT_ID_PATTERN.test(project),
    value: hosting.projectId,
  });
  const connectorName = validateField({
    field: "connector",
    message: unknown,
    test: isGcpServiceAccountId,
    value: hosting.connectorName,
  });
  const serviceAccountId = toServiceAccountId({
    connectorName,
    projectId,
    storedServiceAccount: hosting.connectorServiceAccount,
    unusable: {
      field: "connector",
      message: `P0 doesn't hold a usable service account for this instance's connector. ${REINSTALL_CONNECTOR}`,
    },
  });
  return {
    connectorId,
    connectorServiceAccount: `${serviceAccountId}@${projectId}.iam.gserviceaccount.com`,
    id,
    secretName,
    secretsProjectId,
  };
};

// The two resources below are a copy of the accessor and viewer pair that
// `gcpConnectorTf` grants on its install secret, in
// packages/integrations/vaulted-credential/shared/src/iac.ts. Changed on
// purpose: they name the customer's secret by its ID, not a secret this
// Terraform creates; the member is the connector's account by its email, not
// a resource in the same configuration; and the names carry the instance's
// id.
// TODO(INT-432): share them, and drop this copy.

/**
 * Renders the Terraform that lets one instance's connector read the
 * instance's secret on Google Cloud, and nothing else.
 *
 * It grants two roles on the one secret, never on its project, to the
 * connector's service account. It never declares the secret or a version of
 * it: the customer creates the secret, and its value must not reach Terraform.
 *
 * Every Terraform name carries the instance's id, so the grants of several
 * instances, and their connector's Terraform, can share a configuration.
 */
const renderGcpReadGrantTerraform = (grant: GcpReadGrant): string => {
  const {
    connectorId,
    connectorServiceAccount,
    id: itemId,
    secretName,
    secretsProjectId,
  } = grant;
  const tfName = instanceTfName(itemId);

  return `# Lets P0's Harbor connector read the secret of the Harbor instance
# ${itemId}, and no other.
#
# The secret has to exist already, holding the Harbor admin credential.
# This doesn't create it, and never reads or holds its value. The connector's
# service account has to exist too, so apply the connector's Terraform first.
# Apply this again after the connector's service account is recreated.
#
# If another instance on this connector uses the same secret, the two grants
# are one and the same. Keep it in one instance's Terraform only: destroying it
# takes the connector's access away for both.

locals {
  ${tfName} = {
    # The secret, and the project it's in. P0's installer has the same values.
    secrets_project_id = "${secretsProjectId}"
    secret_name        = "${secretName}"
    # The service account the instance's connector runs as. If the connector's
    # Terraform is in this configuration, use
    # google_service_account.${connectorTfName(connectorId)}.email
    # here instead, so that Terraform creates the account first.
    connector_service_account = "${connectorServiceAccount}"
  }
}

# The connector reads the secret's current value.
resource "google_secret_manager_secret_iam_member" "${tfName}_accessor" {
  project   = local.${tfName}.secrets_project_id
  secret_id = local.${tfName}.secret_name
  role      = "roles/secretmanager.secretAccessor"
  member    = "serviceAccount:\${local.${tfName}.connector_service_account}"
}

# The accessor role gives secretmanager.versions.access only. P0's secret
# client reads the latest version's value and then that version's metadata
# (secretmanager.versions.get), which the accessor role doesn't cover. The
# viewer role does, and can change nothing: on this secret it reads the
# secret's metadata, its versions' metadata and its IAM policy, never a value.
# A custom role with only that permission would have to be created once in the
# secret's project, by someone who can create roles there, and shared by every
# instance.
resource "google_secret_manager_secret_iam_member" "${tfName}_viewer" {
  project   = local.${tfName}.secrets_project_id
  secret_id = local.${tfName}.secret_name
  role      = "roles/secretmanager.viewer"
  member    = "serviceAccount:\${local.${tfName}.connector_service_account}"
}
`;
};

/** An instance's read grant on Google Cloud: the connector's service account
 * may read the instance's secret, and nothing else.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the grant can't be declared with: an id, a project or a secret name
 * that isn't one, or a connector whose service account P0 can't tell or
 * can't use
 */
export const renderGcpReadGrant = (args: GcpReadGrantArgs): IacCommand => {
  const grant = toGcpReadGrant(args);
  return {
    command: renderGcpReadGrantTerraform(grant),
    header: (
      <Paragraph>
        Lets the connector&apos;s service account{" "}
        <Text code>{grant.connectorServiceAccount}</Text> read the secret{" "}
        <Text code>{grant.secretName}</Text> in Google Cloud project{" "}
        <Text code>{grant.secretsProjectId}</Text>, and no other. Apply it after
        the connector&apos;s Terraform, and again if the connector&apos;s
        service account is ever recreated. The secret has to exist already: this
        doesn&apos;t create it, and never holds its value.
      </Paragraph>
    ),
  };
};

/** The name of the inline policy that is an instance's read grant, on its
 * connector's role. It carries the instance's id, so each instance on a
 * connector has its own, and none is one of {@link POLICY_NAMES}. IAM allows
 * a policy name 128 characters, and an id is at most 63. */
const toReadPolicyName = (id: string) => `p0-harbor-${id}-read`;

/** The arguments to {@link renderAwsReadGrants}. */
export type AwsReadGrantArgs = Readonly<{
  /** The id of the connector item the instance names */
  connectorId: string;
  /** Where that connector runs */
  hosting: AwsHosting;
  /** The instance item's id */
  id: string;
  secretName: string;
  /** The secret's account and region. The account may be another than the
   * connector's. */
  vault: AwsVault;
}>;

/** An instance's read grant on AWS, as its Terraform declares it. Each value
 * has passed the rule that makes it safe in the Terraform. */
type AwsReadGrant = Readonly<{
  /** The connector's account */
  accountId: string;
  /** The id of the instance's connector item */
  connectorId: string;
  connectorRegion: string;
  /** The role the connector runs as, which is named after the connector */
  connectorRoleName: string;
  /** The instance item's id */
  id: string;
  /** The secret's account, which may be another than the connector's */
  secretAccountId: string;
  secretName: string;
  /** The secret's ARN without the hyphen and six characters that end it */
  secretPartialArn: string;
  secretsRegion: string;
}>;

/** Whether the secret is in another account than the connector. Then the
 * connector's own policy isn't enough: the secret's account has to allow the
 * connector's role too, and the secret needs a KMS key of the customer's. */
const isCrossAccount = ({ accountId, secretAccountId }: AwsReadGrant) =>
  secretAccountId !== accountId;

/** The ARN of the role the connector runs as. The partition is written out
 * as `aws`, which is right only because the connector's region was held to
 * the commercial partition when the grant was resolved. */
const toConnectorRoleArn = ({ accountId, connectorRoleName }: AwsReadGrant) =>
  `arn:aws:iam::${accountId}:role/${connectorRoleName}`;

/**
 * Resolves the read grant that one instance item's AWS Terraform declares.
 *
 * Every value it returns is written into the Terraform, so each must pass a
 * rule that keeps it inside its string: none of them can hold a quote, a
 * backslash, a line break or `${`. The secret's name also can't hold `*` or
 * `?`, which would widen the grant.
 *
 * @throws ValidationError, with the input's path as its form id, for the first
 * input the grant can't be declared with: an id, a secret name, or a secret's
 * account or region that isn't one; a connector whose id, account, name or
 * region isn't usable; or, for a secret in another account than the
 * connector's, a name the connector couldn't find it by. Its message is for
 * the customer.
 */
const toAwsReadGrant = (args: AwsReadGrantArgs): AwsReadGrant => {
  const { connectorId, hosting, id, secretName: name, vault } = args;
  validateItemId({ id, reinstall: REINSTALL_INSTANCE });
  if (!isItemId(connectorId)) {
    throw new ValidationError(
      "connector",
      {},
      `The ID of this instance's connector can't name Terraform resources. ${REINSTALL_CONNECTOR}`
    );
  }
  const {
    accountId,
    connectorName: connectorRoleName,
    connectorRegion,
  } = toAwsConnectorRole({
    copy: {
      account: `The AWS account ID of this instance's connector isn't 12 digits. ${REINSTALL_CONNECTOR}`,
      field: () => "connector",
      name: `The name of this instance's connector can't name the role it runs as. ${REINSTALL_CONNECTOR}`,
      region: `The region of this instance's connector isn't an AWS region. ${REINSTALL_CONNECTOR}`,
      unsupportedRegion:
        "Harbor supports only connectors in AWS commercial regions for now. Delete this instance, and every other instance that uses the connector, then delete the connector. Add the connector again in a commercial region, then each instance.",
    },
    hosting,
  });
  // The secret's account, region and name are checked as they're stored,
  // and rendered as stored: a padded one is refused rather than trimmed into
  // a grant on another value than the instance holds. INT-394 refuses
  // padding when the instance is created.
  const secretAccountId = validateField({
    exact: true,
    field: "vault.install",
    message: `This instance's secret isn't in an AWS account with a 12-digit ID. ${REINSTALL_INSTANCE}.`,
    test: isAwsAccountId,
    value: vault.install,
  });
  const secretsRegion = validateField({
    exact: true,
    field: "vault.secretsRegion",
    message: `This instance's secret isn't in an AWS region, such as us-west-2. ${REINSTALL_INSTANCE}.`,
    test: isAwsRegion,
    value: vault.secretsRegion,
  });
  if (!isAwsCommercialPartition(secretsRegion)) {
    throw new ValidationError(
      "vault.secretsRegion",
      {},
      `Harbor supports only secrets in AWS commercial regions, such as us-west-2, for now. ${REINSTALL_INSTANCE} with a secret in one.`
    );
  }
  const secretName = validateField({
    exact: true,
    field: "secretName",
    message: `This instance's secret name can't name a secret in AWS Secrets Manager, which takes letters, digits and /_+=.@- up to 512 characters. ${REINSTALL_INSTANCE}.`,
    test: (secret) =>
      findSecretNameProblem(secret, { vault: "aws-sm", by: "name" }) ===
      undefined,
    value: name,
  });
  // P0 points the connector at a secret in another account by its partial
  // ARN, which Secrets Manager can't read when the name ends in a full ARN's
  // suffix. In the connector's own account P0 points it at the secret by its
  // name, as `toHarborInstance` does, so the name's ending doesn't matter. The
  // first sentence is `SUFFIXED_SECRET_NAME`; the second says what to do,
  // since nothing on an instance can be edited.
  if (
    secretAccountId !== accountId &&
    findSecretNameProblem(secretName, {
      vault: "aws-sm",
      by: "partial-arn",
    }) !== undefined
  ) {
    throw new ValidationError(
      "secretName",
      {},
      `Secrets Manager can't find a secret in another account by its name when the name ends in a hyphen and six characters, like -AbCdEf. ${REINSTALL_INSTANCE} with a secret whose name doesn't end that way, or one in the connector's own account.`
    );
  }
  return {
    accountId,
    connectorId,
    connectorRegion,
    connectorRoleName,
    id,
    secretAccountId,
    secretName,
    // The partition is written out as `aws`. That is right only because both
    // regions were just held to the commercial partition: a region in
    // another one needs another partition here, and in the role's ARN.
    secretPartialArn: `arn:aws:secretsmanager:${secretsRegion}:${secretAccountId}:secret:${secretName}`,
    secretsRegion,
  };
};

// The policy below is a copy of the private-key policy in github-repositories'
// connector Terraform (`aws_iam_role_policy "<id>_private_key"` in
// packages/integrations/github-repositories/frontend/src/iac.ts), with its
// locals, aliased provider, `data "aws_partition"`, the comment on
// `kms_key_arn` for each case (`renderKmsKeyComment`) and the precondition
// for a secret in another account (`renderAwsSecretLifecycle`). Changed on
// purpose: it stands alone, in an instance's own Terraform, so the role is
// named by a local rather than read off a resource; the names carry the
// instance's id; and the cross-account precondition doesn't re-derive the
// secret's account, because it's rendered only for a secret in another one.
// TODO(INT-432): share it, and drop this copy.

/** The comment on the `kms_key_arn` local, as `#` lines indented as the
 * locals are. A secret in another account needs a key of the customer's. */
const renderKmsKeyComment = (grant: AwsReadGrant) =>
  (isCrossAccount(grant)
    ? [
        "The ARN of the KMS key that encrypts the secret: the key's own ARN, not",
        `an alias. The secret is in account ${grant.secretAccountId}, so it needs a`,
        "customer-managed key: the default aws/secretsmanager key can't be used",
        "from another account. Set this to that key's ARN. The plan fails until",
        "you do.",
      ]
    : [
        "The ARN of the KMS key that encrypts the secret, if that's a",
        "customer-managed key: the key's own ARN, not an alias. The default",
        "aws/secretsmanager key needs nothing for a secret in this account.",
      ]
  )
    .map((line) => `# ${line}`)
    .join("\n    ");

/**
 * Renders the Terraform, for the connector's account, that lets one
 * instance's connector read the instance's secret on AWS, and nothing else.
 *
 * It adds one inline policy, named after the instance, to the connector's
 * role: `secretsmanager:GetSecretValue` on the one secret, and `kms:Decrypt`
 * through Secrets Manager on the key the customer names, if they name one. It
 * never declares the secret or reads it: the customer creates the secret,
 * and its value must not reach Terraform.
 *
 * For a secret in another account, the customer has to name a key, and the
 * plan fails until they do. That account has to allow the connector's role
 * too: see {@link renderAwsVaultAccountGrantTerraform}.
 *
 * Every Terraform name carries the instance's id, so the grants of several
 * instances, and their connector's Terraform, can share a configuration. The
 * role is named by a literal, so the snippet also stands alone. In a shared
 * configuration that gives Terraform no order between the role and this
 * policy, and the local's comment says what to reference instead.
 */
const renderAwsReadGrantTerraform = (grant: AwsReadGrant): string => {
  const {
    accountId,
    connectorId,
    connectorRegion,
    connectorRoleName,
    id: itemId,
    secretAccountId,
    secretPartialArn,
    secretsRegion,
  } = grant;
  const tfName = instanceTfName(itemId);
  const crossAccount = isCrossAccount(grant);
  const needs = crossAccount
    ? `# The secret is in another AWS account, ${secretAccountId}, so this is one of three
# things to apply. This one goes in the connector's account,
# ${accountId}. The second goes in the secret's account: its resource
# policy. The third is a statement for the policy of the KMS key that encrypts
# the secret, in whichever account holds the key, usually the secret's. The
# connector can read the secret only once all three are in place.
#
# Apply it after the connector's Terraform, which creates the role this adds a
# policy to. This doesn't create the secret, and never reads or holds its
# value.`
    : `# Apply it after the connector's Terraform, which creates the role this adds a
# policy to. It needs no secret to exist yet: until the secret does, the
# connector just has nothing to read. This doesn't create the secret, and
# never reads or holds its value.`;
  const crossAccountCheck = crossAccount
    ? `
    # The secret is in another account, so it needs a key of your own.
    precondition {
      condition     = local.${tfName}.kms_key_arn != null
      error_message = "The Harbor instance's secret is in another AWS account, so it must be encrypted with a customer-managed KMS key. Set kms_key_arn to that key's ARN, and allow the connector's role in the secret's resource policy and in the key's policy."
    }
`
    : "";

  return `# Lets P0's Harbor connector read the secret of the Harbor instance
# ${itemId}, and no other.
#
${needs}
#
# Destroying this removes the one policy from the connector's role. The role
# and the secret stay. Destroy it before the connector's Terraform: Terraform
# doesn't remove a role's other policies when it destroys the role, so that
# destroy fails while this policy is on it.

terraform {
  # The check on kms_key_arn needs Terraform 1.2.
  required_version = ">= 1.2"
}

locals {
  ${tfName} = {
    # The connector's account. P0's installer has the same values.
    account_id       = "${accountId}"
    connector_region = "${connectorRegion}"
    # The role the connector runs as. If the connector's Terraform is in this
    # configuration, use
    # aws_iam_role.${connectorTfName(connectorId)}.name
    # here instead, so that Terraform creates the role first and removes this
    # policy before it.
    connector_role_name = "${connectorRoleName}"
    # The secret that holds the Harbor admin credential, by its partial
    # ARN: its ARN without the hyphen and six random characters that end it.
    secret_partial_arn = "${secretPartialArn}"
    secrets_region     = "${secretsRegion}"
    ${renderKmsKeyComment(grant)}
    kms_key_arn = null
  }
}

# The connector's account. IAM is global, so the region only has to be one the
# account has enabled: the connector's own. Add the credential settings your
# other AWS providers use, such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${tfName}"
  region              = local.${tfName}.connector_region
  allowed_account_ids = [local.${tfName}.account_id]
}

data "aws_partition" "${tfName}" {
  provider = aws.${tfName}
}

# The connector reads this instance's secret, and nothing else. A policy of
# its own, so that each instance on the connector adds one and none replaces
# another's. A role's inline policies share 10,240 characters, which is about
# 40 instances on one connector, or about 20 when each names a key.
resource "aws_iam_role_policy" "${tfName}" {
  provider = aws.${tfName}

  name = "${toReadPolicyName(itemId)}"
  role = local.${tfName}.connector_role_name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = concat(
      [{
        Sid    = "ReadSecret"
        Effect = "Allow"
        Action = "secretsmanager:GetSecretValue"
        # "??????" matches the six random characters that end the secret's
        # ARN, so the connector gets this one secret and no other.
        Resource = "\${local.${tfName}.secret_partial_arn}-??????"
      }],
      local.${tfName}.kms_key_arn == null ? [] : [{
        Sid      = "DecryptSecret"
        Effect   = "Allow"
        Action   = "kms:Decrypt"
        Resource = local.${tfName}.kms_key_arn
        Condition = {
          StringEquals = {
            "kms:ViaService" = "secretsmanager.\${local.${tfName}.secrets_region}.\${data.aws_partition.${tfName}.dns_suffix}"
          }
        }
      }],
    )
  })

  lifecycle {${crossAccountCheck}
    # A key in the secret's region, by its ARN. It may be in another account.
    precondition {
      condition     = local.${tfName}.kms_key_arn == null || can(regex("^arn:aws:kms:\${local.${tfName}.secrets_region}:[0-9]{12}:key/(mrk-)?[0-9a-f-]+$", local.${tfName}.kms_key_arn))
      error_message = "Set kms_key_arn to the ARN of a KMS key in the secret's region, such as arn:aws:kms:<region>:<account>:key/<key id>. An alias, or a key's ID alone, won't do: the policy needs the key's ARN."
    }
  }
}
`;
};

/**
 * Renders the Terraform, for the secret's account, that lets the connector's
 * role in another account read the instance's secret.
 *
 * It sets the secret's resource policy to one statement: the connector's
 * role may call `secretsmanager:GetSecretValue` on this secret. A secret has
 * one resource policy, so this replaces any it already has, and the file
 * says so before anything else.
 *
 * It looks the secret up by its name, which reads the secret's ARN and
 * settings, never its value, and fails the plan if there's no such secret.
 *
 * No precedent: github-repositories only describes this in a comment.
 */
const renderAwsVaultAccountGrantTerraform = (grant: AwsReadGrant): string => {
  const {
    accountId,
    id: itemId,
    secretAccountId,
    secretName,
    secretsRegion,
  } = grant;
  const tfName = vaultTfName(itemId);

  return `# WARNING: THIS REPLACES THE SECRET'S RESOURCE POLICY.
#
# A secret has one resource policy, and applying this sets it to the one
# statement below. If the secret already has a resource policy, don't apply
# this: add the statement below to that policy, where it's managed. To check,
# run:
#   aws secretsmanager get-resource-policy --secret-id ${secretName} --region ${secretsRegion}
# in account ${secretAccountId}. No "ResourcePolicy" in the answer means
# there is none.
#
# Destroying this deletes the secret's resource policy, whatever it then
# holds.
#
# Lets P0's Harbor connector, in account ${accountId}, read the secret of
# the Harbor instance ${itemId}, and no other. It goes in the secret's account,
# ${secretAccountId}: one of the three things a secret in another account
# needs, with the policy on the connector's role and the statement in the
# secret's KMS key policy.
#
# Apply it after the connector's Terraform: AWS refuses a policy that names a
# role that doesn't exist yet. If the connector's role is ever deleted and
# created again, apply this again, and add the key policy statement again:
# AWS keeps the deleted role's unique ID, not its name, so both stop working
# for the new role. The secret has to exist already. This doesn't create it,
# and never reads or holds its value.

locals {
  ${tfName} = {
    # The secret, and the account and region it's in. P0's installer has the
    # same values.
    vault_account_id = "${secretAccountId}"
    secrets_region   = "${secretsRegion}"
    secret_name      = "${secretName}"
    # The role the instance's connector runs as, in the connector's account.
    connector_role_arn = "${toConnectorRoleArn(grant)}"
  }
}

# The secret's account, not the connector's: this provider needs credentials
# for account ${secretAccountId}. Add the credential settings your
# other AWS providers use, such as profile or assume_role, if they have any.
provider "aws" {
  alias               = "${tfName}"
  region              = local.${tfName}.secrets_region
  allowed_account_ids = [local.${tfName}.vault_account_id]
}

# Finds the secret by its name, for its full ARN. This reads the secret's
# settings, never its value.
data "aws_secretsmanager_secret" "${tfName}" {
  provider = aws.${tfName}

  name = local.${tfName}.secret_name
}

# WARNING: this is the secret's whole resource policy. See the top of this
# file before you apply it to a secret that already has one.
resource "aws_secretsmanager_secret_policy" "${tfName}" {
  provider = aws.${tfName}

  secret_arn          = data.aws_secretsmanager_secret.${tfName}.arn
  block_public_policy = true
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Sid       = "P0HarborConnectorRead"
      Effect    = "Allow"
      Principal = { AWS = local.${tfName}.connector_role_arn }
      Action    = "secretsmanager:GetSecretValue"
      Resource  = data.aws_secretsmanager_secret.${tfName}.arn
    }]
  })
}
`;
};

/** The statement that lets the connector's role decrypt a secret in another
 * account, for the customer to add to the policy of the KMS key that
 * encrypts the secret. JSON, not Terraform: a key has one policy, and a
 * Terraform resource for it would replace the whole of it.
 *
 * `Resource` is `*` because in a key's own policy that means the key. The
 * statement has no `Sid`, so two instances' statements can't clash. */
const renderAwsKeyPolicyStatement = (grant: AwsReadGrant): string =>
  JSON.stringify(
    {
      Effect: "Allow",
      Principal: { AWS: toConnectorRoleArn(grant) },
      Action: "kms:Decrypt",
      Resource: "*",
      Condition: {
        StringEquals: {
          // The secret's region was held to the commercial partition, whose
          // DNS suffix is amazonaws.com.
          "kms:ViaService": `secretsmanager.${grant.secretsRegion}.amazonaws.com`,
        },
      },
    },
    undefined,
    2
  );

/**
 * An instance's read grant on AWS: everything that lets the connector's role
 * read the instance's secret, and nothing else.
 *
 * For a secret in the connector's own account that's one snippet, a policy
 * on the connector's role. For a secret in another account it's three: that
 * policy, with the key the customer must name; the secret's resource policy,
 * for the secret's account; and a statement for the policy of the secret's
 * KMS key, as JSON to merge.
 *
 * @throws ValidationError, whose message is for the customer, for the first
 * input the grant can't be declared with: an id, a secret name, or a secret's
 * account or region that isn't one; a connector whose id, account, name or
 * region isn't usable; or, for a secret in another account, a name that ends
 * as a full ARN does
 */
export const renderAwsReadGrants = (args: AwsReadGrantArgs): IacCommand[] => {
  const grant = toAwsReadGrant(args);
  const role = <Text code>{grant.connectorRoleName}</Text>;
  const secret = <Text code>{grant.secretName}</Text>;
  const region = <Text code>{grant.secretsRegion}</Text>;
  const connectorAccount = <Text code>{grant.accountId}</Text>;
  if (!isCrossAccount(grant)) {
    return [
      {
        command: renderAwsReadGrantTerraform(grant),
        header: (
          <Paragraph>
            Lets the connector&apos;s role {role} read the secret {secret} in
            AWS account {connectorAccount}, in {region}, and no other. Apply it
            after the connector&apos;s Terraform. If a KMS key of your own
            encrypts the secret, set <Text code>kms_key_arn</Text> to the
            key&apos;s ARN first. This doesn&apos;t create the secret, and never
            holds its value.
          </Paragraph>
        ),
      },
    ];
  }
  const secretAccount = <Text code>{grant.secretAccountId}</Text>;
  return [
    {
      command: renderAwsReadGrantTerraform(grant),
      header: (
        <Paragraph>
          1 of 3, for the connector&apos;s account, {connectorAccount}. Lets the
          connector&apos;s role {role} read the secret {secret} in AWS account{" "}
          {secretAccount}, in {region}, and no other. A secret in another
          account needs a KMS key of your own: set <Text code>kms_key_arn</Text>{" "}
          to its ARN, or the plan fails. Apply it after the connector&apos;s
          Terraform.
        </Paragraph>
      ),
    },
    {
      command: renderAwsVaultAccountGrantTerraform(grant),
      header: (
        <Paragraph>
          2 of 3, for the secret&apos;s account, {secretAccount}.{" "}
          <Text strong type="danger">
            This replaces the secret&apos;s resource policy.
          </Text>{" "}
          If the secret already has one, don&apos;t apply this: add its
          statement to that policy instead. Otherwise it lets the
          connector&apos;s role read the secret {secret}, and no other. Apply it
          with credentials for account {secretAccount}, after the
          connector&apos;s Terraform.
        </Paragraph>
      ),
    },
    {
      command: renderAwsKeyPolicyStatement(grant),
      header: (
        <Paragraph>
          3 of 3, for the account that holds the secret&apos;s KMS key, usually
          the secret&apos;s, {secretAccount}. This is not Terraform. Add this
          statement to the policy of the KMS key that encrypts the secret,
          beside the statements it already has. Don&apos;t replace the
          key&apos;s policy with it: a key has one policy, and it is what lets
          you manage the key. Add it after the connector&apos;s Terraform, and
          add it again if the connector&apos;s role is ever deleted and created
          again.
        </Paragraph>
      ),
    },
  ];
};
